/**
 * Record-level helpers shared by every `ProjectStore` implementation, so the
 * three backends agree on ids, timestamps, ordering, copy naming and search
 * semantics. Only the storage mechanics differ between them.
 */
import type { CreateProjectInput } from './ProjectStore';
import type { ProjectRecord, ProjectSettings, ProjectSummary } from './types';
import { DEFAULT_PROJECT_SETTINGS } from './types';

const PREVIEW_MAX_LENGTH = 96;

/**
 * A UUID for a new project.
 *
 * `crypto.randomUUID` needs a secure context, which the app has, but tests and
 * a plain-HTTP LAN preview do not always. The fallback still draws from
 * `getRandomValues` where available; the last resort is only ever hit in
 * environments with no WebCrypto at all, where collisions are still
 * vanishingly unlikely at the scale of one browser's project list.
 */
export function newProjectId(): string {
  const webCrypto: Crypto | undefined =
    typeof globalThis.crypto === 'undefined' ? undefined : globalThis.crypto;

  if (webCrypto && typeof webCrypto.randomUUID === 'function') {
    return webCrypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (webCrypto && typeof webCrypto.getRandomValues === 'function') {
    webCrypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  // Pin the version (4) and variant bits so the value is a well-formed UUIDv4.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join('-');
}

export function nowIso(): string {
  return new Date().toISOString();
}

/**
 * Merges a partial patch over the defaults. Written field-by-field rather than
 * with a spread because an explicit `undefined` in the patch must keep the
 * default, not erase it.
 */
export function mergeSettings(patch?: Partial<ProjectSettings>): ProjectSettings {
  if (!patch) return DEFAULT_PROJECT_SETTINGS;
  return {
    direction: patch.direction ?? DEFAULT_PROJECT_SETTINGS.direction,
    expandedFunctions: patch.expandedFunctions
      ? [...patch.expandedFunctions]
      : DEFAULT_PROJECT_SETTINGS.expandedFunctions,
    functionDisplay: patch.functionDisplay ?? DEFAULT_PROJECT_SETTINGS.functionDisplay,
    yesLabel: patch.yesLabel ?? DEFAULT_PROJECT_SETTINGS.yesLabel,
    noLabel: patch.noLabel ?? DEFAULT_PROJECT_SETTINGS.noLabel,
  };
}

/** Builds the record `create()` stores. Pure — no storage access. */
export function buildRecord(input: CreateProjectInput): ProjectRecord {
  const timestamp = nowIso();
  return {
    id: newProjectId(),
    name: normalizeName(input.name),
    source: input.source,
    createdAt: timestamp,
    updatedAt: timestamp,
    settings: mergeSettings(input.settings),
  };
}

/** Trims and falls back, so a project is never nameless in the list. */
export function normalizeName(name: string): string {
  const trimmed = name.trim();
  return trimmed.length > 0 ? trimmed : 'Untitled';
}

function buildPreview(source: string): string {
  const line = source
    .split('\n')
    .map((candidate) => candidate.trim())
    .find((candidate) => candidate.length > 0);
  if (!line) return '';
  return line.length > PREVIEW_MAX_LENGTH ? `${line.slice(0, PREVIEW_MAX_LENGTH - 1)}…` : line;
}

export function toSummary(record: ProjectRecord): ProjectSummary {
  return {
    id: record.id,
    name: record.name,
    updatedAt: record.updatedAt,
    createdAt: record.createdAt,
    sourceLength: record.source.length,
    preview: buildPreview(record.source),
  };
}

/**
 * Newest first. Timestamps are always UTC ISO-8601, so lexicographic order is
 * chronological order; name and id break ties to keep the list stable.
 */
export function compareByRecency(a: ProjectSummary, b: ProjectSummary): number {
  if (a.updatedAt !== b.updatedAt) return a.updatedAt < b.updatedAt ? 1 : -1;
  const byName = a.name.localeCompare(b.name);
  return byName !== 0 ? byName : a.id.localeCompare(b.id);
}

/** Case-insensitive substring match over the name and the pseudocode. */
function matchesQuery(record: ProjectRecord, normalizedQuery: string): boolean {
  if (normalizedQuery.length === 0) return true;
  return (
    record.name.toLowerCase().includes(normalizedQuery) ||
    record.source.toLowerCase().includes(normalizedQuery)
  );
}

/**
 * The one place `list()` and `search()` are implemented; the backends only have
 * to hand over their records. An empty query lists everything.
 */
export function summarize(records: readonly ProjectRecord[], query = ''): ProjectSummary[] {
  const normalized = query.trim().toLowerCase();
  return records
    .filter((record) => matchesQuery(record, normalized))
    .map(toSummary)
    .sort(compareByRecency);
}

const COPY_SUFFIX = /\s*\(copy(?:\s+\d+)?\)$/i;

/**
 * `"Sort"` -> `"Sort (copy)"` -> `"Sort (copy 2)"`.
 *
 * The existing suffix is stripped first so duplicating a duplicate does not
 * produce `"Sort (copy) (copy)"`.
 */
export function copyName(name: string, taken: readonly string[]): string {
  const base = normalizeName(name).replace(COPY_SUFFIX, '').trim() || 'Untitled';
  const used = new Set(taken.map((entry) => entry.toLowerCase()));

  const first = `${base} (copy)`;
  if (!used.has(first.toLowerCase())) return first;

  for (let n = 2; n < 1000; n += 1) {
    const candidate = `${base} (copy ${n})`;
    if (!used.has(candidate.toLowerCase())) return candidate;
  }
  // Pathological case: fall back to something unique rather than colliding.
  return `${base} (copy ${newProjectId().slice(0, 8)})`;
}

/** The copy `duplicate()` writes: same content, fresh identity and timestamps. */
export function buildDuplicate(original: ProjectRecord, taken: readonly string[]): ProjectRecord {
  const timestamp = nowIso();
  return {
    id: newProjectId(),
    name: copyName(original.name, taken),
    source: original.source,
    createdAt: timestamp,
    updatedAt: timestamp,
    settings: mergeSettings(original.settings),
  };
}

/** `put()` copies defensively: callers must not be able to mutate stored state. */
export function cloneRecord(record: ProjectRecord): ProjectRecord {
  return {
    id: record.id,
    name: record.name,
    source: record.source,
    createdAt: record.createdAt,
    updatedAt: record.updatedAt,
    settings: {
      direction: record.settings.direction,
      expandedFunctions: [...record.settings.expandedFunctions],
      functionDisplay: record.settings.functionDisplay,
      yesLabel: record.settings.yesLabel,
      noLabel: record.settings.noLabel,
    },
  };
}
